import Seo from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import FinalCTA from "@/components/sections/FinalCTA";
import { Stagger, StaggerItem, Reveal } from "@/components/motion";
import { SectionHeading } from "@/components/primitives";
import Icon from "@/components/Icon";
import { TRUST } from "@/lib/site";

const PRACTICES = [
  { icon: "Lock", title: "Encryption in transit", desc: "All traffic is protected with modern TLS so your data is secure as it moves." },
  { icon: "KeyRound", title: "Secure authentication", desc: "Hardened sessions, hashed passwords, email verification and trusted social login." },
  { icon: "ShieldCheck", title: "Privacy-first by design", desc: "Your business data is yours. We never sell it, and collect only what's needed." },
  { icon: "UserCheck", title: "You stay in control", desc: "The AI is grounded in your context, and important decisions always remain yours." },
  { icon: "Server", title: "Reliable foundations", desc: "Built on a robust, scalable stack designed to grow with your business." },
  { icon: "FileCheck", title: "Clear policies", desc: "Transparent Privacy Policy and Terms so you always know where you stand." },
];

const COMMITMENTS = [
  "We never sell your personal or business data.",
  "Your business memory powers your AI — and only yours.",
  "You can access, export or delete your data on request.",
  "Consent is explicit for every marketing communication.",
];

export default function Security() {
  return (
    <>
      <Seo
        title="Security & Privacy"
        description="How StUpEco Workspace protects your business data — encryption, secure authentication, privacy-first design and clear data commitments."
        path="/security"
      />
      <PageHeader
        eyebrow="Security & Privacy"
        title="Your business data, protected and private."
        subtitle="Security and privacy aren't features bolted on later — they're built into the foundation of your AI-powered Business Operating System."
      />

      <section className="border-b border-border py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading eyebrow="How we protect you" title="Security practices built into every layer." />
          <Stagger className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {PRACTICES.map((p) => (
              <StaggerItem key={p.title} className="h-full">
                <div className="card-hover flex h-full flex-col gap-4 rounded-2xl border border-border bg-card p-7 hover:border-foreground/20">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-muted/60 text-brand-teal">
                    <Icon name={p.icon} className="h-5 w-5" />
                  </div>
                  <h3 className="font-display text-lg font-semibold tracking-tight">{p.title}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="border-b border-border py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <Reveal>
              <SectionHeading eyebrow="Our commitments" title="Clear promises about your data." />
              <ul className="mt-8 space-y-4">
                {COMMITMENTS.map((c) => (
                  <li key={c} className="flex items-start gap-3 text-sm sm:text-base">
                    <Icon name="Check" className="mt-1 h-4 w-4 shrink-0 text-brand-teal" />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.1} className="grid gap-4 sm:grid-cols-2">
              {TRUST.map((t) => (
                <div key={t.title} className="rounded-2xl border border-border bg-card p-6">
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-muted/60 text-brand-teal">
                    <Icon name={t.icon} className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 font-display text-base font-semibold tracking-tight">{t.title}</h3>
                  <p className="mt-1.5 text-sm text-muted-foreground">{t.desc}</p>
                </div>
              ))}
            </Reveal>
          </div>
        </div>
      </section>
      <FinalCTA />
    </>
  );
}
